// Finance domain types — Campaign Finance POC (Compliance Guardian)

// The itemization missing-fields set is the SAME wire enum the sort inbox uses
// (type-only import — erased at compile, so the finance ↔ sortInbox cycle is inert).
import type { MissingDonorField } from '../generated/enums.gen';
import type { ContributionDto, LinkedBankTransactionDto, LockedPeriodImportCandidateDto, RuleOutcomeDto, TransactionFlagDto } from '../generated/dtos.gen';
import type { WarningCode } from '../generated/errorCodes.gen';
import {
  AccountSelectionStatus,
  ActivationType,
  AmendmentDiffCategory,
  AmendmentDiffChange,
  AmendmentModel,
  BalanceFormShape,
  BankConnectionStatus,
  BankFileFormat,
  BankProvider,
  BankTransactionAttribution,
  ClassificationHintCaution,
  ClassificationHintMatchKind,
  CommitteeType,
  ContactKind,
  ContactType,
  ContributionOrigin,
  CoverPeriodRuleType,
  DeadlineStatus,
  DebtSettlementKind,
  DetectionConfidence,
  DonorGiving,
  DonorSourceCategory,
  ElectionDesignation,
  ElectionOutcomeResult,
  ElectionStage,
  ExpenditureCategory,
  ExpenditureClassificationSubCategory,
  ExpenditureHintProvenance,
  FederalTransferQualification,
  FilingGranularity,
  FilingOrigin,
  FinalReportReason,
  FinanceFlagSeverity,
  FormatKind,
  InKindNature,
  OrganizationType,
  OutcomeType,
  PaymentProcessorKind,
  PreparationMode,
  ReceiptNature,
  RecipientOfficeCategory,
  ReportRowInclusion,
  ReportStatus,
  ReportSummaryKind,
  ResolutionType,
  ScheduleConfigurationGapType,
  ScheduleKind,
  SubmissionPackageExceptionType,
  SyncNowStatus,
} from '../generated/enums.gen';
import type { ExportBatchMemberKind, ObligationKind as FilingObligationKind } from '../generated/enums.gen';

// ============================================================================
// Submission layer enums (FIN-006 / FIN-011)
// ============================================================================

// ============================================================================
// Transaction & Classification enums
// ============================================================================

export enum TransactionStatus {
  Unclassified = 'Unclassified',
  Classified = 'Classified',
  Matched = 'Matched',
  Dismissed = 'Dismissed',
  Reported = 'Reported',
}

export enum TransactionSource {
  Plaid = 'Plaid',
  Square = 'Square',
  PayPal = 'PayPal',
  CSV = 'CSV',
  Manual = 'Manual',
  CsvImport = 'CsvImport',
  ManualEntry = 'ManualEntry',
}

export enum TransactionFlag {
  OverLimit = 'OverLimit',
  ProhibitedSource = 'ProhibitedSource',
  MissingDonorInfo = 'MissingDonorInfo',
  Unmatched = 'Unmatched',
  NeedsReview = 'NeedsReview',
  LateDeposit = 'LateDeposit',
  EarmarkedConduit = 'EarmarkedConduit',
  // ApproachingLimit (the 80%-of-limit warning) was REMOVED. It fired on money that was under the
  // cap and therefore lawful, and neither surface it reached — the treasurer's flag list or the
  // donor page — attached an action to it, so it was noise in front of the signals that mean
  // something. The over-limit flag is what remains.
  LargeContribution = 'LargeContribution',
}

export enum ClassificationType {
  Contribution = 'Contribution',
  /** @deprecated POC only — not supported by backend. Will be removed in FIN-015. */
  Loan = 'Loan',
  Expenditure = 'Expenditure',
  /** @deprecated POC only — not supported by backend. Will be removed in FIN-015. */
  Dismissed = 'Dismissed',
}

// ============================================================================
// Contribution & Expenditure enums
// ============================================================================

export enum ComplianceStatus {
  Valid = 'Valid',
  LimitWarning = 'LimitWarning',
  LimitExceeded = 'LimitExceeded',
  InfoRequired = 'InfoRequired',
  PendingReview = 'PendingReview',
}

export enum ExpenditurePurpose {
  Admin = 'Admin',
  Advertising = 'Advertising',
  Consulting = 'Consulting',
  Fundraising = 'Fundraising',
  Travel = 'Travel',
  Salaries = 'Salaries',
  Food = 'Food',
  Equipment = 'Equipment',
  PrintedMaterials = 'PrintedMaterials',
  ProcessingFees = 'ProcessingFees',
  Other = 'Other',
}

// ============================================================================
// Reporting & Contacts enums
// ============================================================================

/**
 * #711 — treasurer-first option list for the transfer-qualification selector, in the order the
 * treasurer should consider them: the safe default first, then the two exemptions. The labels name
 * the RELATIONSHIP in plain English, never the regulation or the enum.
 */
export const FEDERAL_TRANSFER_QUALIFICATION_OPTIONS: {
  value: FederalTransferQualification;
  label: string;
}[] = [
  {
    value: FederalTransferQualification.None,
    label: 'None of these — treat it as an ordinary contribution',
  },
  {
    value: FederalTransferQualification.SameCandidateAuthorizedCommittee,
    label: 'Another committee authorized by the same candidate',
  },
  {
    value: FederalTransferQualification.JointFundraisingProceeds,
    label: 'This committee’s share of a joint fundraiser',
  },
];

/**
 * #711 — normalizes a transfer-qualification answer to the ONE receipt nature it decides anything
 * for, and turns an unanswered transfer into an explicit {@link FederalTransferQualification.None}.
 * Mirrors the server's `Contribution.NormalizeTransferQualification` so the client sends exactly
 * what the server would store — and so no client can mint an unanswered (legacy, still-exempt)
 * transfer by omitting the field.
 */
export function normalizeTransferQualification(
  nature: ReceiptNature,
  qualification: FederalTransferQualification | null | undefined,
): FederalTransferQualification | null {
  if (nature !== ReceiptNature.FederalTransfer) return null;
  return qualification ?? FederalTransferQualification.None;
}

/**
 * AFU-002 — treasurer-first option list for the in-kind nature selector,
 * shared so the entry, classification, and staged-edit forms render one
 * source of truth (no enum-token leak at the render boundary,
 * `feedback_treasurer_first_copy`). Order matches the AL v6.0 code-table
 * ordering used by the builder. "Other" stays selectable; the AL builder
 * omits an In-Kind "Other" row and alerts the treasurer to enter it in the
 * FCPA portal (`project_al_edi_suffix_inkind_portal_findings`).
 */
export const IN_KIND_NATURE_OPTIONS: { value: InKindNature; label: string }[] = [
  { value: InKindNature.Administrative, label: 'Administrative' },
  { value: InKindNature.Advertising, label: 'Advertising' },
  // "Consulting / Polling" mirrors the AL FCPA code-table wording for code 03
  // (the XML still maps to 03; only the display label follows the portal).
  { value: InKindNature.Consulting, label: 'Consulting / Polling' },
  { value: InKindNature.Equipment, label: 'Equipment' },
  { value: InKindNature.Food, label: 'Food' },
  { value: InKindNature.Rent, label: 'Rent' },
  { value: InKindNature.Transportation, label: 'Transportation' },
  { value: InKindNature.Other, label: 'Other' },
];

// ============================================================================
// Interfaces
// ============================================================================

// `ScenarioTransaction` is the in-memory demo-data row used by the Zustand
// scenario store + Scenario Switcher only. Not a production entity — has no
// API/DB counterpart since the backend `FinanceTransaction` entity was
// retired in BSW-001..012. Renamed in BSW-013 from `FinanceTransaction` to
// remove the namespace collision with the deleted backend entity.
export interface ScenarioTransaction {
  id: string;
  amount: number;
  date: string;
  description: string;
  source: TransactionSource;
  status: TransactionStatus;
  flags: { type: TransactionFlag; severity: FinanceFlagSeverity; message: string; metadata?: Record<string, string> | null }[];
  classifiedAs: ClassificationType | null;
  contributionId: string | null;
  expenditureId: string | null;
  loanId: string | null;
  receiptDate: string | null;
  concurrencyVersion?: number;
}

export interface FinanceContact {
  id: string;
  name: string;
  address: { street: string; city: string; state: string; zip: string };
  employer: string | null;
  occupation: string | null;
  type: ContactType;
  primaryTotal: number;
  generalTotal: number;
  calendarYearTotal: number;
  contributionCount: number;
  paymentCount: number;
  missingRequiredInfo: boolean;
  linkedContactId: string | null;
  concurrencyVersion?: number;
}

export interface Contribution {
  id: string;
  transactionId: string | null;
  contactId: string;
  amount: number;
  date: string;
  type: ReceiptNature;
  electionDesignation: ElectionDesignation;
  complianceStatus: ComplianceStatus;
  overridden: boolean;
  overrideReason: string | null;
  inKindDescription: string | null;
  concurrencyVersion?: number;
  resolutionType?: ResolutionType | null;
  originalContributionId?: string | null;
  resolutionDeadline?: string | null;
}

export interface Expenditure {
  id: string;
  transactionId: string | null;
  contactId: string;
  amount: number;
  date: string;
  purpose: ExpenditurePurpose;
  description: string | null;
  receiptUrl: string | null;
}

/** @deprecated Use FilingDeadlineDto instead — retained for POC component compatibility */
export interface FilingDeadline {
  id: string;
  name: string;
  dueDate: string;
  reportType: string;
  periodStart: string;
  periodEnd: string;
  status: DeadlineStatus;
  reportId: string | null;
}

export interface FilingDeadlineListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  electionType?: string;
}

// ── Obligation Bar (FIP-002) ──

export type ObligationUrgency = 'overdue' | 'imminent' | 'upcoming';

/**
 * Which `FilingObligation` regime a row is (mirrors the backend `ObligationKind` enum).
 * `RapidContribution` (§17-5-8.1(c) $20k Major Contribution) is per-contribution;
 * `DailyAggregate` (§17-5-8(b)(3) $5k/day pre-election report) is per-day, portal-filed;
 * `PreSessionAggregate` (VA §24.2-947.11 pre-session large-contribution report) is a
 * whole-window, multi-donor report of every January gift crossing $1,000 before the
 * General Assembly convenes (`ContributionId`-null, session-date-gated, portal-filed).
 * `RapidNotice` (NC §163-278.9(a)(2) 48-hour notice) is per-contribution like
 * `RapidContribution` but window-gated at creation and portal-native (the `.cfd` artifact
 * is a deferred sibling ticket), so the banner routes it to a single "Mark as reported"
 * control rather than the two-state generate flow.
 * Empty string on the wire for period-deadline obligations (not `FilingObligation`-backed).
 */
export type ObligationKind = FilingObligationKind | '';

export interface ComplianceReport {
  id: string;
  name: string;
  periodStart: string;
  periodEnd: string;
  status: ReportStatus;
  totalContributions: number;
  totalExpenditures: number;
  beginningBalance: number;
  endingBalance: number;
  cashOnHand: number;
  filedDate: string | null;
  confirmationNumber: string | null;
}

// ============================================================================
// Finance Settings API DTOs
// ============================================================================

/**
 * #478 (cluster F) — how the generated filing leaves the app, the served transport axis the
 * finance-UI filing surfaces branch on instead of re-deriving jurisdiction from a raw state code
 * (`stateCode === 'NC'`). `portal` = the treasurer uploads to a named state web portal (AL/VA/LA);
 * `email` = the app emails it to the state inbox (NC → NCSBE); `inSystem` = federal e-file (the
 * `.fec` embeds the certification at generate and the treasurer self-transmits to efo.fec.gov);
 * `agentEntry` = a portal-entry run enters the report in the portal for the treasurer (ADR-093), who
 * still files there, and the `portal` manual path stays offered beside it.
 */
export type FilingTransport = 'portal' | 'email' | 'inSystem' | 'agentEntry';

// ============================================================================
// Finance Contact API DTOs
// ============================================================================

export interface FinanceContactListParams {
  page?: number;
  pageSize?: number;
  type?: ContactType;
  search?: string;
  trafficStatus?: string;
  /** Omitted = everyone. */
  donorGiving?: DonorGiving;
}

// ADR-014 / EFR-003: PairAsTransferResult, the TransferCategory enum,
// MarkAsTransferRequest/Result, SuggestedTransferPairDto, and SuggestedPairDto
// were DELETED with the transfer taxonomy + inter-account pairing subsystem.

export interface ContributionListParams {
  page?: number;
  pageSize?: number;
  contactId?: string;
  electionDesignation?: ElectionDesignation;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================================================
// Expenditure API DTOs (FIN-008)
// ============================================================================

export interface ExpenditureListParams {
  page?: number;
  pageSize?: number;
  contactId?: string;
  category?: ExpenditureCategory;
  dateFrom?: string;
  dateTo?: string;
}

// Compliance outcome helpers
export function getGates(outcomes: RuleOutcomeDto[]): RuleOutcomeDto[] {
  return outcomes.filter((o) => o.outcomeType === OutcomeType.Gate);
}

export function getNotifications(outcomes: RuleOutcomeDto[]): RuleOutcomeDto[] {
  return outcomes.filter((o) => o.outcomeType === OutcomeType.Notification);
}

// Notifications that warn about the ledger rather than announce a filing obligation.
const HEADS_UP_RULE_CODES: ReadonlySet<string> = new Set(['LaterGiftOverLimit']);

export function isHeadsUp(outcome: RuleOutcomeDto): boolean {
  return outcome.outcomeType === OutcomeType.Notification && HEADS_UP_RULE_CODES.has(outcome.ruleCode);
}

// Flags that announce a pre-election notice the treasurer must file. They stay Flags so they never
// mint a rapid-report obligation server-side, but at entry they read as a filing obligation.
const NOTICE_FLAG_RULE_CODES: ReadonlySet<string> = new Set([
  'RapidReportWindowNotice',
  'ExpenditureNoticeRequired',
]);

/** Advisory flags whose message is a pre-election notice due date. */
export function getNoticeFlags(outcomes: RuleOutcomeDto[]): RuleOutcomeDto[] {
  return outcomes.filter(
    (o) => o.outcomeType === OutcomeType.Flag && NOTICE_FLAG_RULE_CODES.has(o.ruleCode),
  );
}

/**
 * Advisory flags that warn the treasurer without blocking the save. Green flags are suggestions
 * (a bank description matching a contact), not warnings, so they are left out.
 */
export function getWarningFlags(outcomes: RuleOutcomeDto[]): RuleOutcomeDto[] {
  return outcomes.filter(
    (o) =>
      o.outcomeType === OutcomeType.Flag &&
      !NOTICE_FLAG_RULE_CODES.has(o.ruleCode) &&
      o.severity !== FinanceFlagSeverity.Green,
  );
}

export interface ComplianceReportListParams {
  page?: number;
  pageSize?: number;
  status?: string;
  deadlineId?: string;
}

/**
 * the rows that appear on the report's itemized schedule: the file's own
 * `Included` set PLUS the already-reported rows folded back into an open report's display
 * (`Reported`). The remaining members are the "in this period but not on this filing"
 * excluded rows. Mirrors the C# `ReportRowInclusionExtensions.IsOnReport` so the two ends
 * agree on what "on the report" means (partition + exhaustive switches).
 */
export function isOnReportInclusion(
  inclusion: ReportRowInclusion,
): inclusion is 'Included' | 'Reported' {
  return inclusion === 'Included' || inclusion === 'Reported';
}

/**
 * #647/#645 — which table `transactionId` names in a re-send request. Mirrors the backend
 * `ExportBatchMemberKind`. The report screen only ever re-sends contributions or expenditures (loans
 * aren't rows on the schedule tables), but the wire type carries all three for parity with the API.
 */
export type TransactionResendKind = ExportBatchMemberKind;

// ============================================================================
// Ruleset source — the shape of apps/api/RuleData/rulesets/{code}.json as served
// verbatim by GET /api/finance/rulesets/{code}/source for the rules viewer.
// Keys starting with "_" are the authors' notes (statute citations, rationale);
// the generated RulesetDetailDto drops them, which is why the viewer reads the file.
// ============================================================================

export interface RulesetSourceContributionLimit {
  officeLevel: string;
  donorType: string;
  qualifierKey?: string;
  limit: number | null;
  trackingPeriod: string;
  electionType?: string | null;
  effectiveDate?: string;
  _comment?: string;
}

export interface RulesetSourceRestriction {
  /** Exactly one of `donorType` and `sourceCategory` is present. */
  donorType?: string;
  sourceCategory?: DonorSourceCategory;
  prohibited: boolean;
  reason: string;
  _comment?: string;
}

export interface RulesetSourceThreshold {
  type: string;
  value?: number | null;
  aggregateThreshold?: number | null;
  unit?: string | null;
  period?: string | null;
  windowBusinessDays?: number | null;
  windowDaysBeforeElection?: number | null;
  windowEndDaysBeforeElection?: number | null;
  daysBefore?: number | null;
  exemptionDays?: number | null;
  sessionStart?: string | null;
  sessionEnd?: string | null;
  electionTypes?: string[] | null;
  officeLevels?: string[] | null;
  windowGatedNotice?: boolean;
  overridable?: boolean;
  _comment?: string;
}

export interface RulesetSourceVersion {
  effectiveDate: string;
  /** Authors' note on the whole limits table (Maryland, North Carolina). */
  _limitNote?: string;
  contributionLimits: RulesetSourceContributionLimit[];
  sourceRestrictions: RulesetSourceRestriction[];
  thresholds: RulesetSourceThreshold[];
}

/** Jurisdiction-wide policy switches. Known nested objects are typed; the rest are scalars/arrays. */
export interface RulesetSourceConfig {
  filingProfile?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface RulesetSource {
  code: string;
  config: RulesetSourceConfig;
  versions: RulesetSourceVersion[];
}

// ============================================================================
// Audit Log types (FIN-034)
// ============================================================================

export interface AuditLogListParams {
  page?: number;
  pageSize?: number;
  entityType?: string;
  entityId?: string;
  action?: string;
  dateFrom?: string;
  dateTo?: string;
}

// ============================================================================
// PPR-003 — File-type detector
// ============================================================================

/**
 * Canonical names the inbox file-type detector returns. Mirrors
 * `apps/api/Common/Constants/AppConstants.cs#DetectedFileKinds`. Free-form
 * string so future processor kinds can be added without a frontend deploy.
 */
export enum DetectedFileKind {
  BankCsv = 'BankCsv',
  BankXlsx = 'BankXlsx',
  BankOfx = 'BankOfx',
  NumeroContributions = 'NumeroContributions',
  NumeroPayouts = 'NumeroPayouts',
  WinRedContributions = 'WinRedContributions',
  WinRedPayouts = 'WinRedPayouts',
  ActBlueContributions = 'ActBlueContributions',
  AnedotContributions = 'AnedotContributions',
  AnedotPayouts = 'AnedotPayouts',
  StripeCombined = 'StripeCombined',
  /**
   * ADR-015 Tier 2 — off-platform hand-compiled sheets routed to the generic
   * content scanner (Source != Kind: kind `Donations` → source
   * `CompiledDonations`). KindPicker-only manual choices — a hand-compiled
   * sheet has no fixed shape, so the detector never auto-pre-fills these.
   */
  Donations = 'Donations',
  Expenses = 'Expenses',
  Unattributed = 'Unattributed',
}

const DETECTED_FILE_KINDS: ReadonlySet<string> = new Set(Object.values(DetectedFileKind));

function isDetectedFileKind(value: string): value is DetectedFileKind {
  return DETECTED_FILE_KINDS.has(value);
}

export function parseDetectedFileKind(value: string | null | undefined): DetectedFileKind | null {
  return value != null && isDetectedFileKind(value) ? value : null;
}

// ============================================================================
// PPR-004 — Bank ingestion
// ============================================================================

// ============================================================================
// PPR-017 — Edit bank profile (future imports only) + Re-attribute import
// ============================================================================

/**
 * Bank-file format (mirrors backend `BankFileFormat`). Listed values are the
 * v1 set; OFX is the SGML/XML statement format banks expose for direct
 * download. Keep in sync with the `BankFileFormat` enum already exported
 * by this module.
 */

// ============================================================================
// BSW-011 — Bank Statement Workspace list endpoint
// ============================================================================

/**
 * BSW-011: status filter for the workspace list endpoint. Mirrors the
 * .NET <c>BankTransactionStatusFilter</c> enum at
 * <c>apps/api/Features/Finance/Services/BankTransactionListService.cs</c>.
 * PascalCase string values so JSON round-trips without a converter.
 * Default in the UI is <c>Unattributed</c> (the inbox-zero shape).
 */
export enum BankTransactionStatusFilter {
  Unattributed = 'Unattributed',
  Matched = 'Matched',
  All = 'All',
  // FSU pivot values — lifecycle lens driven by linked Contribution/
  // Expenditure LedgerStatus rather than BankTransaction.Attribution.
  Staged = 'Staged',
  Reported = 'Reported',
  // ADR-014/016: the off-ramp lens (was 'Withdrawn') — unions ExcludedFromReport
  // and voided money rows. The former Transfer / Dismissed pivots collapsed into
  // it; ADR-016 retired the Attribution=Withdrawn import-undo ghost arm.
  NotReported = 'NotReported',
}

/**
 * BSW-011: sign filter for the workspace list endpoint. Mirrors the
 * .NET <c>BankTransactionSignFilter</c> enum.
 */
export enum BankTransactionSignFilter {
  All = 'All',
  Credits = 'Credits',
  Debits = 'Debits',
}

// ============================================================================
// Portal-entry runs (ADR-093): a run enters a report in the state portal for the treasurer
// ============================================================================
